/**
 * @file submissionService.ts
 * @description
 * Provides functions to create, read, update, and delete submissions (autosave, initialize, status updates, etc.).
 *
 * Key features:
 * - `initializeSubmission(...)`: Creates a new submission in DRAFT status.
 * - `autosaveSubmission(...)`: Autosaves partial form data to an existing submission.
 * - `updateSubmissionStatus(...)`: Changes a submission's status (DRAFT, NEEDS_ACTION, etc.).
 * - `deleteDraftSubmission(...)`: Deletes a submission if it's still in DRAFT status.
 *
 * @dependencies
 * - Supabase client: For database reads/writes.
 * - Logging helpers: For structured logging of key events.
 *
 * @notes
 * - The DRAFT deletion function introduced here only deletes the submission row; we do not remove related documents in this step.
 *   That behavior can be added in a future step if desired.
 * - We rely on row-level security (RLS) so that only the user who owns the submission can delete it.
 * - The `status` check ensures we only delete a submission if it's truly a DRAFT.
 */

import { getSupabaseClient, SubmissionUpdate, SubmissionStatus } from '../lib/supabaseClient';
import { Database } from '../types/supabase';

interface PaymentUpdate {
  submission_id: string;
  submission_type: Database['public']['Enums']['submission_type'];
  updated_at: string;
  billing_first_name?: string | null;
  billing_last_name?: string | null;
  billing_org?: string | null;
  billing_phone_number?: string | null;
  billing_email?: string | null;
}

interface InitialSubmission {
  id: string;
  user_id: string;
  created_at: string;
}

interface AutosavePayload {
  submissionId: string;
  userId: string;
  createdAt: string;
  submittedAt: string | null;
  fields: Record<string, any>; // Contains all form fields including pi_email if present
  status?: string;
}

/**
 * Helper function for structured logging in submission-related operations.
 */
function logSubmission(operation: string, details: Record<string, any>, error?: any) {
  const timestamp = new Date().toISOString();
  const logObj = {
    operation,
    timestamp,
    details,
    ...(error && {
      error:
        typeof error === 'object'
          ? {
              message: error.message,
              name: error.name,
              code: (error as any).code,
              details: (error as any).details,
            }
          : error,
    }),
  };

  if (error) {
    console.error(`SUBMISSION_LOG [${operation}]:`, logObj);
  } else {
    console.log(`SUBMISSION_LOG [${operation}]:`, logObj);
  }

  return logObj;
}

// Use the client
const supabase = getSupabaseClient();

/**
 * initializeSubmission
 * --------------------
 * Creates a new DRAFT submission for a user. Typically called when the user
 * starts a brand new IRB form. You could also adapt this to load an existing
 * draft if one exists.
 *
 * @param userId - The ID of the user creating the submission
 * @param userEmail - The email of the user creating the submission
 * @returns A minimal object with { id, user_id, created_at } for the newly created submission
 */
export const initializeSubmission = async (userId: string, userEmail: string): Promise<InitialSubmission> => {
  logSubmission('initialize-submission', { userId, userEmail });

  try {
    const { data: submission, error } = await supabase
      .from('submissions')
      .insert({
        user_id: userId,
        user_email: userEmail,
        submission_type: 'NEW_SUBMISSION',
        status: 'DRAFT',
        data: {},
      })
      .select('id, user_id, created_at')
      .single();

    if (error) {
      logSubmission('initialize-submission-error', { userId, userEmail }, error);
      throw new Error(`Failed to initialize submission: ${error.message}`);
    }

    if (!submission) {
      const noDataError = new Error('No submission data returned after initialization');
      logSubmission('initialize-submission-no-data', { userId, userEmail }, noDataError);
      throw noDataError;
    }

    logSubmission('initialize-submission-success', {
      userId,
      userEmail,
      submissionId: submission.id,
      created_at: submission.created_at,
    });

    return submission;
  } catch (error) {
    logSubmission('initialize-submission-unexpected', { userId, userEmail }, error);
    throw error;
  }
};

/**
 * autosaveSubmission
 * ------------------
 * Updates the submission record with partial form data (autosave).
 * Also extracts and writes the pi_email to its dedicated column when present.
 * Additionally, creates or updates a payment record with billing information when available.
 * 
 * @param payload - Contains submissionId, userId, fields, etc.
 * @returns Promise<void> or throws an error on failure
 */
export const autosaveSubmission = async (payload: AutosavePayload): Promise<void> => {
  const startTime = Date.now();
  const payloadSize = JSON.stringify(payload.fields).length;

  logSubmission('autosave-start', {
    submissionId: payload.submissionId,
    userId: payload.userId,
    fieldCount: Object.keys(payload.fields).length,
    dataSize: payloadSize,
    isSubmitting: !!payload.submittedAt,
  });

  try {
    // Create the base update object
    const updateObj: SubmissionUpdate = {
      data: payload.fields,
      updated_at: new Date().toISOString(),
      submitted_at: payload.submittedAt
    };

    // Extract pi_email from fields and add to the update object if present
    if (payload.fields.pi_email !== undefined) {
      updateObj.pi_email = payload.fields.pi_email;
      logSubmission('autosave-extracting-pi-email', {
        submissionId: payload.submissionId,
        pi_email: payload.fields.pi_email
      });
    }

    // If status is provided, use it, but ensure it's a valid SubmissionStatus
    if (payload.status) {
      updateObj.status = payload.status as SubmissionStatus;
    }

    logSubmission('autosave-preparing-update', {
      submissionId: payload.submissionId,
      updateSize: JSON.stringify(updateObj).length,
      hasSubmittedAt: !!payload.submittedAt,
      hasPiEmail: 'pi_email' in updateObj
    });

    // Update the submission record
    const { error, data } = await supabase
      .from('submissions')
      .update({
        ...updateObj,
      })
      .eq('id', payload.submissionId)
      .eq('user_id', payload.userId)
      .select();

    if (error) {
      logSubmission(
        'autosave-error',
        {
          submissionId: payload.submissionId,
          userId: payload.userId,
          fieldCount: Object.keys(payload.fields).length,
          dataSize: payloadSize,
          elapsed: Date.now() - startTime,
        },
        error
      );
      throw new Error(`Failed to autosave submission: ${error.message}`);
    }

    // Check if we have billing contact information to save
    const hasBillingInfo = payload.fields.billing_contact_first_name || 
                         payload.fields.billing_contact_last_name || 
                         payload.fields.billing_contact_company || 
                         payload.fields.billing_contact_phone || 
                         payload.fields.invoice_email;

    // Proceed with payment record creation/update if we have billing information
    if (hasBillingInfo) {
      // First, get the submission to check the submission_type
      const { data: submissionData, error: fetchError } = await supabase
        .from('submissions')
        .select('submission_type')
        .eq('id', payload.submissionId)
        .single();

      if (fetchError) {
        logSubmission('autosave-fetch-submission-error', 
          { submissionId: payload.submissionId }, 
          fetchError
        );
        // Continue with autosave process even if payment update fails
      } else {
        // Create payment record update object with proper typing
        const paymentUpdateObj: PaymentUpdate = {
          submission_id: payload.submissionId,
          submission_type: submissionData.submission_type,
          updated_at: new Date().toISOString()
        };

        // Add billing fields if they exist in the payload
        if (payload.fields.billing_contact_first_name) {
          paymentUpdateObj.billing_first_name = payload.fields.billing_contact_first_name;
        }
        if (payload.fields.billing_contact_last_name) {
          paymentUpdateObj.billing_last_name = payload.fields.billing_contact_last_name;
        }
        if (payload.fields.billing_contact_company) {
          paymentUpdateObj.billing_org = payload.fields.billing_contact_company;
        }
        if (payload.fields.billing_contact_phone) {
          paymentUpdateObj.billing_phone_number = payload.fields.billing_contact_phone;
        }
        if (payload.fields.invoice_email) {
          paymentUpdateObj.billing_email = payload.fields.invoice_email;
        }

        logSubmission('autosave-payment-data', {
          submissionId: payload.submissionId,
          paymentData: paymentUpdateObj
        });

        // First check if a payment record already exists for this submission
        const { data: existingPayment, error: searchError } = await supabase
          .from('payments')
          .select('id')
          .eq('submission_id', payload.submissionId)
          .maybeSingle();

        if (searchError) {
          logSubmission('autosave-payment-search-error', 
            { submissionId: payload.submissionId }, 
            searchError
          );
        } else {
          if (existingPayment) {
            // Update existing payment record
            const { error: updateError } = await supabase
              .from('payments')
              .update(paymentUpdateObj)
              .eq('id', existingPayment.id);

            if (updateError) {
              logSubmission('autosave-payment-update-error', 
                { submissionId: payload.submissionId, paymentId: existingPayment.id }, 
                updateError
              );
            } else {
              logSubmission('autosave-payment-update-success', {
                submissionId: payload.submissionId,
                paymentId: existingPayment.id
              });
            }
          } else {
            // Create a new payment record
            const { data: newPayment, error: insertError } = await supabase
              .from('payments')
              .insert(paymentUpdateObj)
              .select('id')
              .single();

            if (insertError) {
              logSubmission('autosave-payment-insert-error', 
                { submissionId: payload.submissionId }, 
                insertError
              );
            } else {
              logSubmission('autosave-payment-insert-success', {
                submissionId: payload.submissionId,
                paymentId: newPayment.id
              });
            }
          }
        }
      }
    }

    if (!data || data.length === 0) {
      logSubmission('autosave-no-rows', {
        submissionId: payload.submissionId,
        userId: payload.userId,
        fieldCount: Object.keys(payload.fields).length,
        elapsed: Date.now() - startTime,
      });
    } else {
      logSubmission('autosave-success', {
        submissionId: payload.submissionId,
        userId: payload.userId,
        fieldCount: Object.keys(payload.fields).length,
        dataSize: payloadSize,
        elapsed: Date.now() - startTime,
      });
    }
  } catch (error) {
    logSubmission(
      'autosave-unexpected',
      {
        submissionId: payload.submissionId,
        userId: payload.userId,
        elapsed: Date.now() - startTime,
      },
      error
    );
    throw error;
  }
};

/**
 * updateSubmissionStatus
 * ----------------------
 * Sets the submission.status to a new value, e.g. 'IRB_REVIEW', 'DECISION_ISSUED', etc.
 *
 * @param submissionId - The submission we want to update
 * @param newStatus    - The new status: 'DRAFT' | 'NEEDS_ACTION' | 'AWAITING_PI_APPROVAL' | 'IRB_REVIEW' | 'DECISION_ISSUED'
 * @throws Error if update fails
 *
 * @example
 * await updateSubmissionStatus('abcdef-1234...', 'IRB_REVIEW');
 */
export async function updateSubmissionStatus(
  submissionId: string,
  newStatus: SubmissionStatus
): Promise<void> {
  logSubmission('update-submission-status', { submissionId, newStatus });

  try {
    const { data, error } = await supabase
      .from('submissions')
      .update({ status: newStatus, updated_at: new Date().toISOString() })
      .eq('id', submissionId)
      .select();

    if (error) {
      logSubmission('update-submission-status-error', { submissionId, newStatus }, error);
      throw new Error(`Failed to update submission status: ${error.message}`);
    }

    if (!data || data.length === 0) {
      const noRowsError = new Error('No rows were updated. Possibly invalid submission ID or RLS rules prevented update.');
      logSubmission('update-submission-status-no-rows', { submissionId, newStatus }, noRowsError);
      throw noRowsError;
    }

    logSubmission('update-submission-status-success', { submissionId, newStatus });
  } catch (err) {
    logSubmission('update-submission-status-unexpected', { submissionId, newStatus }, err);
    throw err;
  }
}

/**
 * deleteDraftSubmission
 * ---------------------
 * Deletes a submission row if it's owned by the user and is still in DRAFT status.
 *
 * @param submissionId - The ID of the submission to delete
 * @param userId       - The ID of the user who owns the submission
 * @returns Promise<void> - resolves if deletion succeeded, otherwise throws
 *
 * @description
 * - Ensures we only delete the row if status='DRAFT'.
 * - If no row is found or it's not DRAFT, it throws an error.
 * - Does not delete or modify any documents; the documents remain in DB & storage.
 * - Future expansions could remove or mark those documents as inactive.
 */
export async function deleteDraftSubmission(submissionId: string, userId: string): Promise<void> {
  logSubmission('delete-draft-submission-start', { submissionId, userId });

  try {
    // We can either check status in a SELECT query or do a conditional DELETE with a match on (status='DRAFT').
    // We'll do a conditional DELETE so no second query is required.
    const { data, error } = await supabase
      .from('submissions')
      .delete()
      .eq('id', submissionId)
      .eq('user_id', userId)
      .eq('status', 'DRAFT')
      .select();

    if (error) {
      logSubmission('delete-draft-submission-error', { submissionId, userId }, error);
      throw new Error(`Failed to delete draft submission: ${error.message}`);
    }

    if (!data || data.length === 0) {
      const noRowsError = new Error(
        'No draft submission found with the specified ID (either does not exist, not owned by you, or not in DRAFT).'
      );
      logSubmission('delete-draft-submission-none', { submissionId, userId }, noRowsError);
      throw noRowsError;
    }

    logSubmission('delete-draft-submission-success', {
      submissionId,
      userId,
      deletedRows: data.length,
    });
  } catch (err) {
    logSubmission('delete-draft-submission-unexpected', { submissionId, userId }, err);
    throw err;
  }
}
